import { useEffect, useState } from 'react';
import {
  REGION_KINDS,
  WINE_COLORS,
  WINE_LOOKUP_LABELS,
  WINE_WORLDS,
  WINE_WORLD_LABELS,
  type LookupAdminRow,
  type RegionKind,
  type WineColor,
  type WineLookupKind,
  type WineWorld,
} from '@gwc/shared';
import { ApiError } from '../../lib/api';
import { Button, Dialog, Input, Select } from '../ui';
import { LookupPicker } from './LookupPicker';
import { REGION_KIND_LABELS, draftFrom, type LookupDraft } from './setupHelpers';

/**
 * Add an entry, or edit one's details: its name, and where it sits — a region's
 * parent, side of the world and kind; a vineyard's or producer's region; a
 * grape's colour.
 */
export function LookupEditDialog({
  open,
  kind,
  row,
  onClose,
  onSubmit,
  onMergeInstead,
}: {
  open: boolean;
  kind: WineLookupKind;
  /** null = adding a new entry. */
  row: LookupAdminRow | null;
  onClose: () => void;
  onSubmit: (draft: LookupDraft) => Promise<void>;
  onMergeInstead?: (existing: { id: string; name: string }) => void;
}) {
  const [draft, setDraft] = useState<LookupDraft>(() => draftFrom(kind, row));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(draftFrom(kind, row));
      setError(null);
      setExisting(null);
    }
  }, [open, kind, row]);

  const one = WINE_LOOKUP_LABELS[kind].one;
  const set = (patch: Partial<LookupDraft>) => setDraft((d) => ({ ...d, ...patch }));

  async function submit(e?: { preventDefault: () => void }) {
    e?.preventDefault();
    if (!draft.name.trim()) {
      setError('Enter a name');
      return;
    }
    setBusy(true);
    setError(null);
    setExisting(null);
    try {
      await onSubmit(draft);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'lookup_exists') {
        setExisting(err.details as { id: string; name: string });
      }
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={row ? `Edit ${row.name}` : `Add ${one.toLowerCase()}`}
      description={
        row
          ? undefined
          : 'Added here, it is ready to pick straight away — no review needed.'
      }
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {row ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <Input
          label="Name"
          value={draft.name}
          onChange={(e) => set({ name: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit(e);
          }}
          maxLength={200}
        />

        {kind === 'regions' ? (
          <>
            <LookupPicker
              kind="regions"
              label="Sits inside"
              value={draft.parent}
              onChange={(parent) => set({ parent })}
              excludeIds={row ? [row.id] : []}
              clearLabel="Top level"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="World"
                value={draft.world ?? ''}
                onChange={(e) => set({ world: (e.target.value || null) as WineWorld | null })}
                hint="Which side of the wine form offers it"
              >
                <option value="">Either (unclassified)</option>
                {WINE_WORLDS.map((w) => (
                  <option key={w} value={w}>
                    {WINE_WORLD_LABELS[w]}
                  </option>
                ))}
              </Select>
              <Select
                label="Kind"
                value={draft.regionKind ?? ''}
                onChange={(e) => set({ regionKind: (e.target.value || null) as RegionKind | null })}
              >
                <option value="">Not set</option>
                {REGION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {REGION_KIND_LABELS[k]}
                  </option>
                ))}
              </Select>
            </div>
          </>
        ) : kind === 'vineyards' || kind === 'producers' ? (
          <LookupPicker
            kind="regions"
            label={kind === 'vineyards' ? 'Region / commune' : 'Home region'}
            value={draft.parent}
            onChange={(parent) => set({ parent })}
            clearLabel="Not placed"
          />
        ) : (
          <Select
            label="Colour"
            value={draft.color ?? ''}
            onChange={(e) => set({ color: (e.target.value || null) as WineColor | null })}
          >
            <option value="">Not set</option>
            {WINE_COLORS.map((c) => (
              <option key={c} value={c}>
                {c === 'rose' ? 'Rosé' : c[0]!.toUpperCase() + c.slice(1)}
              </option>
            ))}
          </Select>
        )}

        {error ? (
          <div className="rounded-md border border-danger/40 px-3 py-2 text-sm text-danger" role="alert">
            {error}
            {existing && row && onMergeInstead ? (
              <div className="mt-2">
                <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => onMergeInstead(existing)}>
                  Merge {row.name} into {existing.name}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}
